/* ==================================================================
   nilstyle — homepage section behaviour
   Self-contained (no dependency on Dawn's SliderComponent / animations).
   - <nil-carousel> custom element: horizontal slider with prev/next
   - reveal-on-scroll for .nil-reveal
   - brand-story scrollytelling ([data-nil-story])
   Theme-editor aware: custom element re-inits on section reload; the
   function-based inits listen for shopify:section:load / :unload.
================================================================== */
(() => {
  const prefersReduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------------
     Carousel
  ----------------------------------------------------------------- */
  class NilCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-nil-carousel-track]');
      if (!this.track) return;
      const scope = this.closest('.nil-section') || this.parentElement || this;
      this.prevBtn = scope.querySelector('[data-nil-carousel-prev]');
      this.nextBtn = scope.querySelector('[data-nil-carousel-next]');

      this._onScroll = () => this.updateButtons();
      this._onResize = () => this.updateButtons();
      this._onPrev = () => this.scrollByStep(-1);
      this._onNext = () => this.scrollByStep(1);

      this.prevBtn?.addEventListener('click', this._onPrev);
      this.nextBtn?.addEventListener('click', this._onNext);
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize);
      this.updateButtons();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
    }

    step() {
      const item = this.track.querySelector(':scope > *');
      if (!item) return this.track.clientWidth * 0.8;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
      return item.getBoundingClientRect().width + gap;
    }

    scrollByStep(dir) {
      this.track.scrollBy({ left: dir * this.step(), behavior: 'smooth' });
    }

    updateButtons() {
      const max = this.track.scrollWidth - this.track.clientWidth - 1;
      if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.track.scrollLeft >= max;
    }
  }
  if (!customElements.get('nil-carousel')) {
    customElements.define('nil-carousel', NilCarousel);
  }

  /* ----------------------------------------------------------------
     Gallery (main image + thumbnail strip)
  ----------------------------------------------------------------- */
  class NilGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-nil-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-nil-gallery-thumb]'));
      if (!this.thumbs.length) return;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const idx = Number(thumb.dataset.nilGalleryThumb);
          this.activate(idx);
        });
      });
    }

    activate(idx) {
      this.slides.forEach((s) =>
        s.classList.toggle('is-active', Number(s.dataset.nilGallerySlide) === idx)
      );
      this.thumbs.forEach((t) => {
        const on = Number(t.dataset.nilGalleryThumb) === idx;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-current', on ? 'true' : 'false');
      });
    }
  }
  if (!customElements.get('nil-gallery')) {
    customElements.define('nil-gallery', NilGallery);
  }

  /* ----------------------------------------------------------------
     Reveal-on-scroll
  ----------------------------------------------------------------- */
  function initReveal(root) {
    const items = root.querySelectorAll('.nil-reveal:not([data-nil-revealed])');
    if (!items.length) return;
    if (prefersReduced || !('IntersectionObserver' in window)) {
      items.forEach((el) => {
        el.dataset.nilRevealed = '1';
        el.classList.add('is-revealed');
      });
      return;
    }
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-revealed');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    items.forEach((el) => {
      el.dataset.nilRevealed = '1';
      io.observe(el);
    });
  }

  /* ----------------------------------------------------------------
     Brand story (scrollytelling)
  ----------------------------------------------------------------- */
  function initStory(root) {
    const stories =
      root.matches && root.matches('[data-nil-story]')
        ? [root]
        : root.querySelectorAll('[data-nil-story]');
    stories.forEach((story) => {
      if (story.dataset.nilStoryBound) return;
      story.dataset.nilStoryBound = '1';

      const chapters = Array.from(story.querySelectorAll('[data-nil-story-chapter]'));
      const images = Array.from(story.querySelectorAll('[data-nil-story-img]'));
      const dots = Array.from(story.querySelectorAll('[data-nil-story-dot]'));
      if (!chapters.length) return;

      const setActive = (idx) => {
        images.forEach((im) =>
          im.classList.toggle('is-active', im.dataset.nilStoryImg === String(idx))
        );
        dots.forEach((d) =>
          d.classList.toggle('is-active', d.dataset.nilStoryDot === String(idx))
        );
      };

      if (prefersReduced || !('IntersectionObserver' in window)) {
        setActive(0);
        return;
      }

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) setActive(e.target.dataset.nilStoryChapter);
          });
        },
        { rootMargin: '-40% 0px -40% 0px' }
      );
      chapters.forEach((c) => io.observe(c));
      story._nilStoryObserver = io;
    });
  }

  /* ----------------------------------------------------------------
     Header drawer close (the in-drawer X)
     The X is a <span role="button"> (not a <button>) so Dawn's MenuDrawer
     does NOT auto-bind it to closeSubmenu — which would leave body scroll
     locked. Instead we click the drawer summary, routing through
     onSummaryClick -> closeMenuDrawer for a full, animated close.
  ----------------------------------------------------------------- */
  function initDrawerClose(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.nil-drawer__close').forEach((el) => {
      if (el.dataset.nilBound) return;
      el.dataset.nilBound = '1';
      const close = () => {
        const summary = el.closest('header-drawer')?.querySelector('summary.header__icon--menu');
        if (summary && summary.closest('details')?.hasAttribute('open')) summary.click();
      };
      el.addEventListener('click', close);
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          close();
        }
      });
    });
  }

  /* ----------------------------------------------------------------
     Wiring
  ----------------------------------------------------------------- */
  function initAll(root) {
    initReveal(root);
    initStory(root);
    initDrawerClose(root);
  }

  if (document.readyState !== 'loading') {
    initAll(document);
  } else {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  }

  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    const story = e.target.querySelector && e.target.querySelector('[data-nil-story]');
    if (story && story._nilStoryObserver) story._nilStoryObserver.disconnect();
  });
})();
